import { useQuery } from '@apollo/client';
import { useAuthState } from '@auth';
import type { Community } from '@communities/community/domain';
import { useCurrentCommunityId } from '@communities/community/hooks/useCurrentCommunityId';
import { transformErrors } from '@core/adapters/errors';
import { UserRoles, type DomainError } from '@core/domain';
import { EXTENDED_GRAPHQL_TIMEOUT_MS } from '@core/services/constants';
import { useMemo } from 'react';

import { GET_COMMUNITY_FOR_AUDIT } from '../graphql/getCommunityForAudit';

interface CommunityResponse {
  community: Community;
}

/**
 * Hook for fetching community details specifically for audit pages
 *
 * Uses GET_COMMUNITY_FOR_AUDIT query which excludes moderationStats to avoid
 * dependency on the Moderation Service. This prevents audit pages from failing
 * when moderation statistics are unavailable or return invalid data.
 *
 * @returns Community detail data, loading state, errors, and refetch function
 */
export function useCommunityDetailForAudit() {
  const communityId = useCurrentCommunityId();
  const { hasRoleAccess } = useAuthState();

  const { data, loading, error, refetch } = useQuery<CommunityResponse>(GET_COMMUNITY_FOR_AUDIT, {
    variables: { id: communityId },
    skip: !communityId,
    fetchPolicy: 'cache-first',
    context: { timeout: EXTENDED_GRAPHQL_TIMEOUT_MS },
  });

  const community = data?.community || null;

  // Enhanced role checking with hierarchical support
  const isCommunityAdmin = useMemo(() => {
    if (!community) return false;

    return hasRoleAccess(UserRoles.ADMIN, community.communityGroupId, community.id);
  }, [community, hasRoleAccess]);

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  return {
    community,
    isCommunityAdmin,
    loading,
    errors,
    refetch,
  };
}
